import { useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/api.service';
import { formatDateTimeAr, formatMoneyAr } from './loan-financial.format';
import { SectionCard, SkeletonBlock } from './LoanUiBits';

type TimelineItem = {
  id: string;
  at: string;
  event: string;
  user?: string;
  amount?: number | null;
  linkLabel?: string;
  linkHref?: string;
};

type Props = {
  loan: any;
  enabled: boolean;
};

function actor(u?: { firstName?: string; lastName?: string; email?: string } | null) {
  if (!u) return undefined;
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return name || u.email || undefined;
}

/** Timeline aditivo: entidades del préstamo + audit si hay permiso. No es event sourcing. */
export function LoanTimeline({ loan, enabled }: Props) {
  const [auditItems, setAuditItems] = useState<TimelineItem[]>([]);
  const [loadingAudit, setLoadingAudit] = useState(false);

  const localItems = useMemo((): TimelineItem[] => {
    const items: TimelineItem[] = [];
    if (!loan) return items;

    items.push({
      id: 'created',
      at: String(loan.createdAt ?? ''),
      event: 'Préstamo creado',
      user: actor(loan.sourceApplication?.createdBy) ?? actor(loan.seller),
    });

    if (loan.sourceApplication?.reviewedAt) {
      items.push({
        id: 'approved',
        at: String(loan.sourceApplication.reviewedAt),
        event: 'Solicitud revisada / aprobada',
        user: actor(loan.sourceApplication.reviewedBy),
      });
    }

    for (const d of loan.disbursements ?? []) {
      if (d.disbursedAt) {
        items.push({
          id: `disb-${d.id}`,
          at: String(d.disbursedAt),
          event: 'Desembolsado',
          user: actor(d.disbursedBy),
          amount: Number(loan.amount ?? 0),
        });
      }
    }

    for (const p of loan.payments ?? []) {
      const voided = Boolean(p.voidedAt) || p.status === 'VOIDED';
      items.push({
        id: `pay-${p.id}`,
        at: String(p.paymentDate ?? p.createdAt ?? ''),
        event: voided ? 'Pago anulado' : 'Pago',
        amount: Number(p.amount ?? 0),
        user: actor(p.receivedBy) ?? actor(p.createdBy),
        linkLabel: 'Ver pagos',
        linkHref: '#pagos',
      });
      if (voided && p.voidedAt) {
        items.push({
          id: `void-${p.id}`,
          at: String(p.voidedAt),
          event: 'Pago anulado (registro)',
          amount: Number(p.amount ?? 0),
          user: actor(p.voidedBy),
        });
      }
    }

    if (loan.earlyPayoffAt) {
      items.push({
        id: 'payoff-confirmed',
        at: String(loan.earlyPayoffAt),
        event: 'Cancelación anticipada confirmada',
        amount: loan.earlyPayoffAmount != null ? Number(loan.earlyPayoffAmount) : null,
      });
    }

    return items
      .filter((i) => i.at)
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }, [loan]);

  useEffect(() => {
    if (!enabled || !loan?.id) return;
    let cancelled = false;
    const load = async () => {
      setLoadingAudit(true);
      try {
        const res = await apiService.getAuditLogsByEntity('Loan', loan.id);
        const logs = Array.isArray(res) ? res : res?.logs ?? res?.data ?? [];
        if (cancelled) return;
        setAuditItems(
          (logs as any[]).slice(0, 50).map((l, idx) => ({
            id: `audit-${l.id ?? idx}`,
            at: String(l.createdAt ?? l.timestamp ?? ''),
            event: String(l.action ?? l.operation ?? 'Evento de auditoría'),
            user: l.actorEmail ?? l.actor?.email ?? undefined,
          })),
        );
      } catch {
        if (!cancelled) setAuditItems([]);
      } finally {
        if (!cancelled) setLoadingAudit(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [enabled, loan?.id]);

  const merged = useMemo(() => {
    const all = [...localItems, ...auditItems];
    const seen = new Set<string>();
    return all
      .filter((i) => {
        const key = `${i.at}|${i.event}|${i.amount ?? ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return Boolean(i.at);
      })
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
      .slice(0, 80);
  }, [localItems, auditItems]);

  return (
    <SectionCard title="Historial" subtitle="Eventos relevantes del préstamo (audit + entidades).">
      {loadingAudit ? <SkeletonBlock className="h-10 mb-3" /> : null}
      {merged.length === 0 ? (
        <p className="text-sm text-slate-500">Sin eventos para mostrar.</p>
      ) : (
        <ol className="space-y-3">
          {merged.map((item) => (
            <li key={item.id} className="border-l-2 border-slate-200 pl-3">
              <p className="text-xs text-slate-500">{formatDateTimeAr(item.at)}</p>
              <p className="text-sm font-medium text-slate-900">{item.event}</p>
              <div className="text-xs text-slate-600 flex flex-wrap gap-3">
                {item.user ? <span>{item.user}</span> : null}
                {item.amount != null ? (
                  <span className="tabular-nums">{formatMoneyAr(item.amount)}</span>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      )}
    </SectionCard>
  );
}
